import React, { FC, useState, useMemo, useContext } from 'react';
import { PageSpinner } from '@td-client-frontend/common';
import clsx from 'clsx';
import MarketingSearchNavigation from '../MarketingSearchNavigation';
import Profile from './MarketingPreferencesForm';
import { useGetMarketingProfileById } from '../../../hooks/useMarketingProfile';
import { Nullable } from '../../../../../internal/src/@types/common';
import { MarketingHierarchyRowData, MarketingProfileById } from '../../../@types/Marketing';
import { ExcludeFromMarketing } from '../components/ExcludeFromMarketing';
import MarketingPreferencesFooter from '../components/MarketingPreferencesFooter';
import { PendingChangesContext, PendingChangesContextProvider } from '../../../context/PendingChangesContext';
import { alwaysSet } from '../../../utils/marketingUtils';

const MarketingPreferences: FC = () => {
  const [selectedHierarchyNode, setSelectedHierarchyNode] = useState<Nullable<MarketingHierarchyRowData>>(null);
  const [isProfileValid, setIsProfileValid] = useState<boolean>(false);
  const [profileData, setProfileData] = useState<MarketingProfileById>();

  const { isChanged, setIsChanged } = useContext(PendingChangesContext);
  const { isFetching } = useGetMarketingProfileById({
    id: selectedHierarchyNode?.assignedMarketingProfile?.id.toString() || '',
    onSuccess: setProfileData
  });

  const isExcluded = useMemo(
    () => !!(selectedHierarchyNode?.marketingExclusion || selectedHierarchyNode?.ancestorMarketingExclusion),
    [selectedHierarchyNode]
  );

  const handleProfileDataChange = (data: MarketingProfileById, isChanged = true) => {
    setIsChanged(isChanged);
    setProfileData(data);
  };

  return (
    <section
      className="flex flex-col w-full px-6 sm:px-2 md:px-6 pt-4 max-h-min"
      data-testid="marketing-preferences-section"
    >
      <h1 className="ml-2 text-4xl font-effra">Marketing Preferences</h1>
      <div className="flex flex-col sm:flex-row pt-4 h-full border-gray-20">
        <MarketingSearchNavigation selectedNode={selectedHierarchyNode} onNodeChange={setSelectedHierarchyNode} />
        <div className="w-full sm:w-3/5">
          {!profileData || isFetching || !selectedHierarchyNode ? (
            isFetching ? (
              <div className="w-full min-h-fit flex items-center justify-center">
                <PageSpinner />
              </div>
            ) : (
              <p className="flex-1 ml-8 font-effra text-lg">
                Search and make a selection to view and edit preferences.
              </p>
            )
          ) : (
            <section
              style={window.matchMedia('(min-width: 768px)').matches ? { maxHeight: 'calc(100vh - 200px)' } : {}}
              className={clsx(
                'px-8 flex-1 border-t-1 pt-8 border-gray-20 overflow-visible',
                'md:px-8',
                'sm:px-0 sm:pl-4 sm:pr-2 sm:border-none sm:pt-0 sm:overflow-y-auto',
                ''
              )}
              data-testid="marketing-preferences-data-section"
            >
              <ExcludeFromMarketing groupOrgData={selectedHierarchyNode} isExcluded={isExcluded} />
              <Profile
                profileData={profileData}
                groupOrgData={selectedHierarchyNode}
                isReadOnly={isExcluded}
                onValid={alwaysSet(setIsProfileValid, true)}
                onInvalid={alwaysSet(setIsProfileValid, false)}
                onChange={handleProfileDataChange}
              />
              <MarketingPreferencesFooter
                groupOrgData={selectedHierarchyNode}
                profileData={profileData}
                disabled={!isProfileValid || !isChanged}
                isExcluded={isExcluded}
              />
            </section>
          )}
        </div>
      </div>
    </section>
  );
};

export const MarketingPreferencesWithPendingChanges = () => (
  <PendingChangesContextProvider>
    <MarketingPreferences />
  </PendingChangesContextProvider>
);
